import React, { useEffect, useRef } from 'react';
import { brokerMasterApi, companiesApi, commonApi, schemesApi, supportingDataApi, clientMasterApi } from '@/services/api';
import { formatSchemeLabel } from '@/utils/formatters';
import styles from '@/components/utils/shared.module.css';

/**
 * Date controls and the option loaders that feed them.
 *
 * The select controls themselves live in `@/components/ui`
 * (`FilterSelect`, `FilterMultiSelect`, `FilterAsyncSelect`,
 * `FilterAsyncMultiSelect`) — this module only supplies the `loadOptions`
 * functions they take, plus the two date filters the kit has no equivalent for.
 */
export interface FilterOption {
  value: string;
  label: string;
}

// ---- Last Working Date Filter --------------------------------------------

interface LastWorkingDateFilterProps {
  value: string;
  onChange: (date: string) => void;
  label?: string;
  inputId?: string;
  max?: string;
  min?: string;
}

export const LastWorkingDateFilter: React.FC<LastWorkingDateFilterProps> = ({
  value,
  onChange,
  label = 'Date',
  inputId = 'lwd-filter',
  max,
  min,
}) => {
  const onChangeRef = useRef(onChange);
  useEffect(() => { onChangeRef.current = onChange; });

  useEffect(() => {
    if (value) return;
    let ignore = false;
    const today = new Date().toISOString().split('T')[0];
    commonApi.getLastWorkingDay()
      .then(data => { if (!ignore) onChangeRef.current(data.last_working_day || today); })
      .catch(() => { if (!ignore) onChangeRef.current(today); });
    return () => { ignore = true; };
  }, []);

  return (
    <div className={styles['filter-group']}>
      <label htmlFor={inputId}>{label}</label>
      <input
        id={inputId}
        type="date"
        value={value}
        onChange={e => onChange(e.target.value)}
        className={styles.input}
        min={min}
        max={max}
      />
    </div>
  );
};

// ---- Date Range Filter ---------------------------------------------------

interface DateRangeFilterProps {
  startDate: string;
  endDate: string;
  onStartDateChange: (date: string) => void;
  onEndDateChange: (date: string) => void;
  startLabel?: string;
  endLabel?: string;
  startId?: string;
  endId?: string;
  // Upper bound (YYYY-MM-DD) applied to both inputs — e.g. a date-access ceiling
  // for restricted users. Omit for no restriction.
  max?: string;
  // Lower bound (YYYY-MM-DD) applied to both inputs.
  min?: string;
}

export const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  startLabel = 'From Date',
  endLabel = 'To Date',
  startId = 'start-date',
  endId = 'end-date',
  max,
  min,
}) => {
  const onStartRef = useRef(onStartDateChange);
  const onEndRef = useRef(onEndDateChange);
  useEffect(() => { onStartRef.current = onStartDateChange; onEndRef.current = onEndDateChange; });

  useEffect(() => {
    if (startDate || endDate) return;
    let ignore = false;
    const today = new Date().toISOString().split('T')[0];
    commonApi.getLastWorkingDay()
      .then(data => {
        if (ignore) return;
        onStartRef.current(data.last_working_day || today);
        onEndRef.current(today);
      })
      .catch(() => {
        if (ignore) return;
        onStartRef.current(today);
        onEndRef.current(today);
      });
    return () => { ignore = true; };
  }, []);

  return (
    <>
      <div className={styles['filter-group']}>
        <label htmlFor={startId}>{startLabel}</label>
        <input
          id={startId}
          type="date"
          value={startDate}
          onChange={e => onStartDateChange(e.target.value)}
          className={styles.input}
          max={max}
          min={min}
        />
      </div>
      <div className={styles['filter-group']}>
        <label htmlFor={endId}>{endLabel}</label>
        <input
          id={endId}
          type="date"
          value={endDate}
          onChange={e => onEndDateChange(e.target.value)}
          className={styles.input}
          max={max}
          min={min}
        />
      </div>
    </>
  );
};

// ---- Option loaders ------------------------------------------------------
// Pass these to FilterAsyncSelect / FilterAsyncMultiSelect from @/components/ui.

/** Dropdown use: PMS-only, matching useClientApi — AIF-only accounts must not appear in selects. */
export const loadClientOptions = async (inputValue: string): Promise<FilterOption[]> => {
  try {
    const response = await clientMasterApi.getClients({
      search: inputValue,
      page_size: 500,
      client_type: 'PMS' as const,
    });
    return response.results.map((c: { id: number; client_code: string; full_name?: string; first_name?: string; last_name?: string }) => ({
      value: c.id.toString(),
      label: `${c.client_code} - ${c.full_name || `${c.first_name} ${c.last_name}`}`,
    }));
  } catch {
    return [];
  }
};

export const loadCompanyOptions = async (inputValue: string): Promise<FilterOption[]> => {
  try {
    const response = await companiesApi.getCompanies({ search: inputValue, page_size: 500 });
    return response.results.map(c => ({
      value: c.id.toString(),
      label: `${c.symbol} - ${c.name}`,
    }));
  } catch {
    return [];
  }
};

export const loadBrokerOptions = async (inputValue: string): Promise<FilterOption[]> => {
  try {
    const response = await brokerMasterApi.list({
      search: inputValue,
      page_size: 500,
      is_active: true,
      ordering: 'broker_name',
    });
    const brokers = response.results ?? response;
    return brokers.map((b: { id: number; broker_name: string }) => ({
      value: b.id.toString(),
      label: b.broker_name,
    }));
  } catch {
    return [];
  }
};

/** Investment strategies as select options, value = PK.
 *
 * `inputValue` filters client-side: the master list is short (a handful of strategies)
 * and the endpoint takes no search parameter, so one fetch beats a round trip per keypress.
 */
export const loadInvestmentStrategyOptions = async (
  inputValue: string,
): Promise<FilterOption[]> => {
  try {
    const strategies = await supportingDataApi.getInvestmentStrategies();
    const query = inputValue.trim().toLowerCase();
    return strategies
      .map((s: { id: number; code: string; name: string }) => ({
        value: s.id.toString(),
        label: `${s.code} - ${s.name}`,
      }))
      .filter(option => !query || option.label.toLowerCase().includes(query));
  } catch {
    return [];
  }
};

export const loadSchemeOptions = async (inputValue: string): Promise<FilterOption[]> => {
  try {
    const response = await schemesApi.getSchemes({
      search: inputValue,
      page_size: 50,
      is_active: true,
      ordering: 'code',
    });
    return response.results.map(s => ({
      value: s.id.toString(),
      label: formatSchemeLabel(s),
    }));
  } catch {
    return [];
  }
};
